import { useMemo, useCallback } from 'react';

import type { ExperienceAttributes, ExperienceTrackerAPI } from '@confluence/experience-tracker';
import type { ContentStatus, MoreActions } from '@confluence/content-types-header';
import { useSidePanelStateActions } from '@confluence/side-panel-state-container';
import {
	contentBoardStatusService,
	contentMoreActionsService,
} from '@confluence/content-types-bridge';
import { useThemingService } from '@confluence/whiteboard-common';

import type { DatabaseOnboardingService } from '../services/database-onboarding-service';
import type {
	ResolvedCommentHandler,
	DatabaseResolvedCommentsService,
} from '../services/database-resolved-comment-service';

declare global {
	interface Window {
		databaseBridge?: {
			onboardingService: DatabaseOnboardingService;
			updateBoardStatus: (status: ContentStatus) => void;
			updateMoreActions: (items: MoreActions) => void;
			/*
			 * A hook for the database iframe to handle when database body loads successfully.
			 */
			onContentLoadSucceeded: (attributes: ExperienceAttributes) => void;
			/**
			 * A hook for the database iframe to handle when database body loads unsuccessfully.
			 */
			onContentLoadFailed: (error: Error, attributes: ExperienceAttributes) => void;
			/**
			 * This should be called when fetching the current loaded version name (will return null on latest version)
			 * @returns {string | null} The name of the current version, as it should be shown in CFE
			 */
			getVersionName: () => string | null;
			/**
			 * A hook that enables the database iframe to dismiss the Confluence side panel which currently houses version history
			 * This can be reused in the future when more features are added to the side panel
			 */
			onCloseConfluenceSidePanel: () => void | undefined;
		};
	}
}

interface UseDatabaseBridgeMethodsProps {
	experienceTracker: ExperienceTrackerAPI;
	onboardingService: DatabaseOnboardingService;
	resolvedCommentService: DatabaseResolvedCommentsService;
	segmentMetric: {
		stop: () => void;
		cancel: () => void;
	};
	experienceName: string;
	versionHistoryExperienceName: string;
	versionId: string | null;
	getVersionName: () => string | null;
}

export const useDatabaseBridgeMethods = ({
	experienceTracker,
	onboardingService,
	resolvedCommentService,
	segmentMetric,
	experienceName,
	versionHistoryExperienceName,
	versionId,
	getVersionName,
}: UseDatabaseBridgeMethodsProps) => {
	const { themingService } = useThemingService();
	const { hideSidePanel } = useSidePanelStateActions();

	const onContentLoadSucceeded = useCallback(
		(attributes: ExperienceAttributes) => {
			experienceTracker.succeed({
				name: experienceName,
				attributes,
			});
			if (versionId) {
				experienceTracker.succeed({
					name: versionHistoryExperienceName,
					attributes,
				});
			}
			segmentMetric.stop();
		},
		[experienceTracker, experienceName, segmentMetric, versionId, versionHistoryExperienceName],
	);

	const onContentLoadFailed = useCallback(
		(error: Error, attributes: ExperienceAttributes) => {
			error.message = `Canvas Database error: ${error.message}`;

			experienceTracker.stopOnError({
				name: experienceName,
				attributes,
				error,
			});
			segmentMetric.cancel();
		},
		[experienceTracker, experienceName, segmentMetric],
	);

	const onCloseConfluenceSidePanel = useCallback(() => {
		hideSidePanel();
	}, [hideSidePanel]);

	return useMemo(
		() => ({
			onContentLoadSucceeded,
			onContentLoadFailed,
			updateBoardStatus: (status: ContentStatus) => {
				contentBoardStatusService.updateActions(status);
			},
			updateMoreActions: (items: MoreActions) => {
				contentMoreActionsService.updateActions(items);
			},
			onboardingService,
			onThemeChange: (callback: () => void) => {
				themingService.onThemeChange(callback);
			},
			registerOnReopenResolvedComment: (cb: ResolvedCommentHandler) => {
				resolvedCommentService.registerOnReopenResolvedComment(cb);
			},
			getVersionName,
			onCloseConfluenceSidePanel,
		}),
		[
			onContentLoadSucceeded,
			onContentLoadFailed,
			onboardingService,
			themingService,
			resolvedCommentService,
			getVersionName,
			onCloseConfluenceSidePanel,
		],
	);
};
